import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { MessageCircle, Globe, Mail, Phone, Instagram } from "lucide-react";

const INSTAGRAM_URL =
  "https://www.instagram.com/richcubewealthmanagement?xtok=MXRucDB5eHQ4ems3bg==";
const EMAIL = "richcubemarketing@gmail.com";

const LOGO =
  "https://customer-assets-cm19k8pv.emergentagent.net/wingman/4be0c1cd-8cc3-450f-ab9d-6230338a9741/attachments/dbc363331499412bba1d8b502390e8c0_image.bin";

const linkCls =
  "text-white font-semibold hover:text-[#C9A84C] hover:underline underline-offset-4 decoration-[#C9A84C] decoration-2 transition-colors";

const socialCls =
  "w-10 h-10 inline-flex items-center justify-center rounded-full border-2 border-white/40 text-white hover:bg-[#C9A84C] hover:text-[#0B2545] hover:border-[#C9A84C] transition-colors";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer
      data-testid="footer"
      className="bg-[#061528] text-white mt-24 border-t-4 border-[#C9A84C]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img
              src={LOGO}
              alt="Richcube"
              className="h-[90px] md:h-[100px] w-auto bg-white rounded-md p-2 object-contain"
            />
            <div>
              <div className="font-serif text-xl text-white tracking-wide font-bold">
                {t.brand}
              </div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A84C] font-bold">
                {t.brand_tagline}
              </div>
            </div>
          </div>
          <p className="mt-6 text-sm leading-relaxed max-w-md text-white font-medium">
            {t.footer.about_text}
          </p>

          <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
            <a
              href="https://wa.me/919203809569"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="WhatsApp"
              data-testid="footer-social-whatsapp"
              className={socialCls}
            >
              <MessageCircle size={16} strokeWidth={2.5} />
            </a>
            <a
              href="https://richcube.in"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Richcube website"
              data-testid="footer-social-web"
              className={socialCls}
            >
              <Globe size={16} strokeWidth={2.5} />
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Instagram"
              data-testid="footer-social-instagram"
              className={socialCls}
            >
              <Instagram size={16} strokeWidth={2.5} />
            </a>
            <a
              href={`mailto:${EMAIL}`}
              aria-label="Email"
              data-testid="footer-social-email"
              className={socialCls}
            >
              <Mail size={16} strokeWidth={2.5} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-[#C9A84C] text-xs uppercase tracking-[0.22em] mb-5 font-bold">
            {t.footer.quick}
          </h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" className={linkCls}>{t.nav.home}</Link></li>
            <li><Link to="/properties" className={linkCls}>{t.nav.properties}</Link></li>
            <li><Link to="/properties?exclusive=1" className={linkCls}>{t.nav.exclusive}</Link></li>
            <li><Link to="/properties?assured=1" className={linkCls}>{t.nav.assured_return}</Link></li>
            <li><Link to="/project/ghd-velvet-vista" className={linkCls}>GHD Velvet Vista</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-[#C9A84C] text-xs uppercase tracking-[0.22em] mb-5 font-bold">
            {t.footer.contact}
          </h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2 text-white font-semibold">
              <Phone size={14} strokeWidth={2.5} className="text-[#C9A84C]" />
              <a
                href="https://wa.me/919203809569"
                target="_blank"
                rel="noreferrer noopener"
                className={linkCls}
                data-testid="footer-whatsapp"
              >
                +91 92038 09569
              </a>
            </li>
            <li className="flex items-center gap-2 text-white font-semibold">
              <Globe size={14} strokeWidth={2.5} className="text-[#C9A84C]" />
              <a
                href="https://richcube.in"
                target="_blank"
                rel="noreferrer noopener"
                className={linkCls}
                data-testid="footer-main-site"
              >
                {t.footer.main_site}
              </a>
            </li>
            <li className="flex items-center gap-2 text-white font-semibold">
              <Globe size={14} strokeWidth={2.5} className="text-[#C9A84C]" />
              <a
                href="https://richcubeproperties.com"
                className={linkCls}
                data-testid="footer-domain"
              >
                richcubeproperties.com
              </a>
            </li>
            <li className="flex items-center gap-2 text-white font-semibold">
              <Mail size={14} strokeWidth={2.5} className="text-[#C9A84C]" />
              <a
                href={`mailto:${EMAIL}`}
                className={linkCls}
                data-testid="footer-email"
              >
                {EMAIL}
              </a>
            </li>
            <li className="flex items-center gap-2 text-white font-semibold">
              <Instagram size={14} strokeWidth={2.5} className="text-[#C9A84C]" />
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer noopener"
                className={linkCls}
                data-testid="footer-instagram"
              >
                @richcubewealthmanagement
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t-2 border-[#C9A84C]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs flex flex-col md:flex-row justify-between gap-2">
          <span className="text-white/85 font-semibold">{t.footer.rights}</span>
          <span className="text-white/85 font-semibold">
            Powered by{" "}
            <a
              href="https://richcube.in"
              target="_blank"
              rel="noreferrer noopener"
              className="text-[#C9A84C] font-bold hover:underline underline-offset-4 decoration-[#C9A84C] decoration-2"
            >
              richcube.in
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
